// @flow

import uuidv4 from 'uuid/v4'
import { DataGridDuck } from 'react-redux-components'

import authenticatedRequest from './authenticated-request'
import { closeSystemSidebar } from './app'
import { addSuccessNotification } from './notification-manager'

import type { Dispatch, GetState, ReduxAction } from './index'
import type { NetError } from './authenticated-request'

export const PARSE_STATUS_PENDING: 'xlsxuploader/PENDING_PARSE' = 'xlsxuploader/PENDING_PARSE'
export const PARSE_STATUS_SUCCESS: 'xlsxuploader/SUCCESS_PARSE' = 'xlsxuploader/SUCCESS_PARSE'
export const PARSE_STATUS_ERROR: 'xlsxuploader/ERROR_PARSE' = 'xlsxuploader/ERROR_PARSE'
export const PARSE_DATA_RESET: 'xlsxuploader/PARSE_DATA_RESET' = 'xlsxuploader/PARSE_DATA_RESET'
export const SET_SUBMIT_PERCENT: 'xlsxuploader/PENDING_PERCENT_SET' = 'xlsxuploader/PENDING_PERCENT_SET'
export const SET_SUBMIT_PENDING: 'xlsxuploader/PENDING_SUBMIT_SET' = 'xlsxuploader/PENDING_SUBMIT_SET'

export const CHUNK_UPLOAD_PENDING: 'xlsxuploader/CHUNK_UPLOAD_PENDING' = 'xlsxuploader/CHUNK_UPLOAD_PENDING'
export const CHUNK_UPLOAD_SUCCESS: 'xlsxuploader/CHUNK_UPLOAD_SUCCESS' = 'xlsxuploader/CHUNK_UPLOAD_SUCCESS'
export const CHUNK_UPLOAD_ERROR: 'xlsxuploader/CHUNK_UPLOAD_ERROR' = 'xlsxuploader/CHUNK_UPLOAD_ERROR'

type ACTION_PARSE_STATUS_PENDING = {| type: typeof PARSE_STATUS_PENDING, payload: string |}
type ACTION_PARSE_STATUS_SUCCESS = {|
  type: typeof PARSE_STATUS_SUCCESS,
  payload: {| rows: Array<Object>, cols: Array<string>, message: string |},
|}
type ACTION_PARSE_STATUS_ERROR = {| type: typeof PARSE_STATUS_ERROR, payload: string |}
type ACTION_PARSE_DATA_RESET = {| type: typeof PARSE_DATA_RESET |}

type ACTION_SET_SUBMIT_PERCENT = {| type: typeof SET_SUBMIT_PERCENT, payload: number |}
type ACTION_SET_SUBMIT_PENDING = {| type: typeof SET_SUBMIT_PENDING, payload: boolean |}

type ACTION_CHUNK_UPLOAD_PENDING = {| type: typeof CHUNK_UPLOAD_PENDING |}
type ACTION_CHUNK_UPLOAD_SUCCESS = {| type: typeof CHUNK_UPLOAD_SUCCESS |}
type ACTION_CHUNK_UPLOAD_ERROR = {| type: typeof CHUNK_UPLOAD_ERROR, payload: NetError |}

export type XLSXUploaderAction =
  | ACTION_PARSE_STATUS_PENDING
  | ACTION_PARSE_STATUS_SUCCESS
  | ACTION_PARSE_STATUS_ERROR
  | ACTION_PARSE_DATA_RESET
  | ACTION_SET_SUBMIT_PERCENT
  | ACTION_SET_SUBMIT_PENDING
  | ACTION_CHUNK_UPLOAD_PENDING
  | ACTION_CHUNK_UPLOAD_SUCCESS
  | ACTION_CHUNK_UPLOAD_ERROR

export const statuses = {
  NEW: 'NEW',
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
}

type Statuses = 'NEW' | 'PENDING' | 'SUCCESS' | 'ERROR'
type XLSXUploaderState_ = {|
  parseStatus: Statuses,
  statusMessage: string,
  parseData: {|
    cols: Array<string>,
    rows: Array<Object>,
  |},
  submitPending: boolean,
  submitPercent: number,
  uploadStatus: Statuses,
|}
export type XLSXUploaderState = $Shape<XLSXUploaderState_>
export const initialState = {
  parseStatus: statuses.NEW,
  statusMessage: '',
  parseData: {
    cols: [],
    rows: [],
  },
  submitPending: false,
  submitPercent: 0,
  uploadStatus: statuses.NEW,
}

export const reducer = (state: XLSXUploaderState = initialState, action: ReduxAction) => {
  switch (action.type) {
    case PARSE_STATUS_PENDING:
      return {
        ...state,
        parseStatus: statuses.PENDING,
        statusMessage: action.payload,
        parseData: { cols: [], rows: [] },
      }
    case PARSE_STATUS_SUCCESS:
      return {
        ...state,
        parseStatus: statuses.SUCCESS,
        statusMessage: action.payload.message,
        parseData: { cols: action.payload.cols, rows: action.payload.rows },
      }
    case PARSE_STATUS_ERROR:
      return { ...state, parseStatus: statuses.ERROR, statusMessage: action.payload }

    case PARSE_DATA_RESET:
      return { ...state, parseStatus: statuses.NEW, statusMessage: '', parseData: { cols: [], rows: [] } }

    case SET_SUBMIT_PENDING:
      return { ...state, submitPending: action.payload }
    case SET_SUBMIT_PERCENT:
      return { ...state, submitPercent: action.payload }

    case CHUNK_UPLOAD_PENDING:
      return { ...state, uploadStatus: statuses.PENDING }
    case CHUNK_UPLOAD_SUCCESS:
      return { ...state, uploadStatus: statuses.SUCCESS }
    case CHUNK_UPLOAD_ERROR:
      return {
        ...state,
        uploadStatus: statuses.ERROR,
        parseStatus: statuses.ERROR,
        statusMessage: action.payload.response.data.message,
      }

    default:
      return state
  }
}

export default reducer

//Creators
export const setParsePending = (msg: string): ACTION_PARSE_STATUS_PENDING => ({
  type: PARSE_STATUS_PENDING,
  payload: msg,
})
export const setParseSuccess = (
  cols: Array<string>,
  rows: Array<Object>,
  message: string
): ACTION_PARSE_STATUS_SUCCESS => ({
  type: PARSE_STATUS_SUCCESS,
  payload: { cols, rows, message },
})
export const setParseError = (msg: string): ACTION_PARSE_STATUS_ERROR => ({ type: PARSE_STATUS_ERROR, payload: msg })
export const resetParseData = (): ACTION_PARSE_DATA_RESET => ({ type: PARSE_DATA_RESET })
export const setSubmitPending = (isPending: boolean): ACTION_SET_SUBMIT_PENDING => ({
  type: SET_SUBMIT_PENDING,
  payload: isPending,
})
export const setSubmitPercent = (percent: number): ACTION_SET_SUBMIT_PERCENT => ({
  type: SET_SUBMIT_PERCENT,
  payload: percent,
})

export const uploadData = (postURL: string, chunkSize: number) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    let xlsxData = getState().xlsxUploader.parseData
    let flowId = uuidv4()
    let dataLength = xlsxData.rows.length
    let cols = xlsxData.cols
    let start = 0

    dispatch(setSubmitPercent(0))
    dispatch(setSubmitPending(true))

    while (start < dataLength) {
      let rows = xlsxData.rows.slice(start, start + chunkSize)
      let lastChunk = start + chunkSize >= xlsxData.rows.length
      dispatch({ type: CHUNK_UPLOAD_PENDING })
      try {
        await authenticatedRequest.post(postURL, { flowId, cols, rows, lastChunk })
        dispatch({ type: CHUNK_UPLOAD_SUCCESS })
      } catch (err) {
        dispatch({ type: CHUNK_UPLOAD_ERROR, payload: err })
        dispatch(setSubmitPending(false))
        return
      }
      start += chunkSize
      dispatch(setSubmitPercent(start / dataLength * 100))
    }
    dispatch(setSubmitPending(false))

    let dataGridModel = /visits/.test(postURL) ? 'visitsGrid' : 'panelGrid'
    dispatch(DataGridDuck.creators.resetDatagrid(dataGridModel))

    dispatch(closeSystemSidebar())
    dispatch(addSuccessNotification('Caricamento dati effettuato con successo'))
  }
}



// WEBPACK FOOTER //
// ./src/store/xlsx-uploader.js